import { useEffect, useRef, useState } from 'react'
import { ApiClientError, type UserView } from '@olmt/shared'
import { createDirectChat, createGroupChat, loadDirectory } from '../lib/data'
import { useT } from '../lib/i18n'
import { useAuthStore } from '../stores/authStore'
import { Avatar } from './Avatar'
import { IconButton } from './IconButton'
import { Spinner } from './Spinner'
import { CloseIcon } from './Icons'

type NewChatPickerProps = {
  onClose: () => void
  /** The created (or existing, for a repeat DM) chat id. */
  onCreated: (chatId: string) => void
}

/**
 * "New chat" over `GET /api/users` (CONTRACT §4 *Directory*). One member selected ⇒ a DM, via the
 * same `createDirectChat` a console's per-member "Message" button could call; two or more ⇒ a
 * group with an optional title. The signed-in user is filtered out — a self-DM is rejected by
 * `POST /api/chats`.
 */
export function NewChatPicker({ onClose, onCreated }: NewChatPickerProps) {
  const t = useT()
  const meId = useAuthStore((state) => state.user?.id ?? null)
  const [users, setUsers] = useState<UserView[] | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    loadDirectory()
      .then((list) => {
        if (!cancelled) setUsers(list.filter((user) => user.id !== meId))
      })
      .catch(() => {
        if (!cancelled) setError(t('chat.couldNotLoadMembers'))
      })
    return () => {
      cancelled = true
    }
  }, [meId])

  useEffect(() => {
    dialogRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggle = (userId: string) => {
    setSelected((previous) =>
      previous.includes(userId) ? previous.filter((id) => id !== userId) : [...previous, userId],
    )
  }

  const create = async () => {
    const [first, ...rest] = selected
    if (!first) return
    setBusy(true)
    setError(null)
    try {
      const chatId =
        rest.length === 0
          ? await createDirectChat(first)
          : await createGroupChat(selected, title.trim() || undefined)
      onCreated(chatId)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : t('chat.couldNotCreateChat'))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-app/80 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-chat-heading"
        tabIndex={-1}
        className="sheet-enter flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-panel bg-surface pb-[var(--sab)] outline-none shadow-lift ring-1 ring-line-strong sm:rounded-panel sm:pb-0"
      >
        <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <h2 id="new-chat-heading" className="min-w-0 flex-1 truncate text-title font-semibold text-ink">
            {t('chat.newChat')}
          </h2>
          <IconButton label={t('chat.close')} size={36} onClick={onClose}>
            <CloseIcon size={18} />
          </IconButton>
        </header>

        <p role="status" className="px-4 pt-3 text-ui text-muted">
          {selected.length === 0 ? t('chat.pickMembersHint') : t('chat.selected', { count: selected.length })}
        </p>

        {selected.length > 0 && (
          <ul aria-label={t('chat.selectedMembers')} className="flex flex-wrap gap-1.5 px-4 pt-2">
            {selected.map((id) => {
              const member = users?.find((candidate) => candidate.id === id)
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => toggle(id)}
                    className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-accent/15 px-3 text-meta font-medium text-accent ring-1 ring-accent/30 ring-inset"
                  >
                    <span className="max-w-40 truncate">
                      <bdi>{member?.displayName ?? t('chat.memberFallback')}</bdi>
                    </span>
                    <CloseIcon size={14} />
                    <span className="sr-only">{t('chat.removeFromSelection')}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {selected.length > 1 && (
          <label className="flex flex-col gap-1.5 px-4 pt-3 text-meta text-muted">
            {t('chat.groupNameOptional')}
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={80}
              placeholder={t('chat.groupNamePlaceholder')}
              className="min-h-11 rounded-well bg-surface2 px-3 text-body text-ink ring-1 ring-line ring-inset placeholder:text-muted"
            />
          </label>
        )}

        {error && (
          <p role="alert" className="px-4 pt-2 text-meta text-danger">
            {error}
          </p>
        )}

        <div className="chat-scroll mt-2 min-h-0 flex-1">
          {users === null ? (
            <div className="flex justify-center p-6">
              <Spinner label={t('chat.loadingMembers')} />
            </div>
          ) : users.length === 0 ? (
            <p className="p-6 text-center text-ui text-muted">{t('chat.noOtherMembers')}</p>
          ) : (
            <ul className="flex flex-col p-2">
              {users.map((user) => {
                const picked = selected.includes(user.id)
                return (
                  <li key={user.id}>
                    <label
                      className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-panel px-2 py-2 transition-colors duration-quick ${
                        picked ? 'bg-surface2' : 'hover:bg-surface2/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="size-5 shrink-0 accent-accent"
                        checked={picked}
                        onChange={() => toggle(user.id)}
                      />
                      <Avatar name={user.displayName} path={user.avatarUrl} size={40} online={user.online} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-ui text-ink">
                          <bdi>{user.displayName}</bdi>
                        </span>
                        <span className="block truncate text-meta text-muted">@{user.handle}</span>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-line p-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 flex-1 rounded-full text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2"
          >
            {t('chat.cancel')}
          </button>
          <button
            type="button"
            disabled={selected.length === 0 || busy}
            onClick={() => void create()}
            className="min-h-11 flex-1 rounded-full bg-accent px-4 text-ui font-semibold press text-on-accent hover:bg-accent-strong disabled:opacity-40"
          >
            {busy ? t('chat.creating') : selected.length > 1 ? t('chat.createGroup') : t('chat.openChat')}
          </button>
        </div>
      </div>
    </div>
  )
}
